import { useState } from 'react';
import { SegmentGroup } from 'semantic-ui-react';
import { SuiTable, SuiTableState } from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';

import { Permission, OdpMonitoringListResult } from 'api';
import { NextMonitoringDateLabel, useExaminerId } from 'components';
import {
  useOdpMonitoringListQuery,
  useOdpNextMonitoringDateQuery,
  useBulkDeleteOdpMonitoringMutation,
} from 'store';
import { columns } from './OdpMonitoringList.columns';
import { MonitoringTableProvider, MonitoringTableActions } from '../common';

const DEFAULT_EMPTY_LIST: OdpMonitoringListResult[] = [];
const INITIAL_TABLE_STATE: SuiTableState<OdpMonitoringListResult> = {
  pageIndex: 0,
  pageSize: 10,
  sortBy: [],
};

export const OdpMonitoringList = () => {
  const examinerId = useExaminerId();
  const { permissions } = usePermissions();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const { data, isFetching } = useOdpMonitoringListQuery(examinerId);

  const { data: nextMonitoringDate } = useOdpNextMonitoringDateQuery({ examinerId });
  const [bulkDelete] = useBulkDeleteOdpMonitoringMutation();

  const showAddButton = permissions.includes(Permission.AddOdp);
  const canDelete = permissions.includes(Permission.DeleteOdp);

  const handleDelete = async (selectedIds: Set<number>) => {
    await bulkDelete({ ids: Array.from(selectedIds), examinerId }).unwrap();
    setSelectedIds(new Set());
  };

  return (
    <MonitoringTableProvider selectedIds={selectedIds} onSelectionChange={setSelectedIds}>
      <SegmentGroup>
        <MonitoringTableActions
          onDelete={handleDelete}
          hasArchiveButton={false}
          hasDeleteButton={canDelete}
          hasAddButton={showAddButton}
        >
          <NextMonitoringDateLabel date={nextMonitoringDate} />
        </MonitoringTableActions>

        <SuiTable
          disableFilters
          disableSortBy
          initialState={INITIAL_TABLE_STATE}
          data={data ?? DEFAULT_EMPTY_LIST}
          loading={isFetching}
          columns={columns}
          tableRowProps={(_, { row }) => ({
            negative: row.original.isDeleted,
            className: row.original.isDeleted ? 'deleted-record' : '',
          })}
        />
      </SegmentGroup>
    </MonitoringTableProvider>
  );
};
